import { PracticeArea, PracticeAreaCreateDto, PracticeAreaUpdateDto } from 'types/practice-areas';
import { portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';
import { filteredAndSortedQuery } from './util/filteredAndSortedQuery';

const APIPracticeAreasSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['PracticeAreas', 'ActivePracticeAreas', 'Notifications', 'TenantConfiguration'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getPracticeAreaById: builder.query<PracticeArea, string>({
                query: (entityId) => `/v1/practice-areas/${entityId}`,
                keepUnusedDataFor: 0,
            }),
            getPracticeAreasList: builder.query<PracticeArea[], void>({
                query: () => '/v1/practice-areas',
                providesTags: ['PracticeAreas'],
            }),
            getPracticeAreasWithFilters: accumulatedQueryWithFilters<PracticeArea>(builder, '/v1/practice-areas', {
                sort: 'fullPath,asc',
                providesTags: ['PracticeAreas'],
            }),
            // The practice areas list's own read: a matching subarea brings its parent along. The pickers keep
            // getPracticeAreasWithFilters.
            getPracticeAreasListPage: accumulatedQueryWithFilters<PracticeArea>(builder, '/v1/practice-areas', {
                extraParams: { includeParents: 'true' },
                providesTags: ['PracticeAreas'],
            }),
            getActivePracticeAreasWithFilters: accumulatedQueryWithFilters<PracticeArea>(
                builder,
                '/v1/practice-areas',
                {
                    sort: 'fullPath,asc',
                    passiveFilters: 'isActive==true',
                    providesTags: ['PracticeAreas'],
                }
            ),
            getPracticeAreasWithFiltersNotPaginated: filteredAndSortedQuery<PracticeArea>(
                builder,
                '/v1/practice-areas',
                {
                    providesTags: ['PracticeAreas'],
                }
            ),
            // Every write refreshes the tenant configuration, which counts the practice areas excluded from automatic
            // closing.
            addPracticeArea: builder.mutation<PracticeArea, PracticeAreaCreateDto>({
                query: ({ ...body }) => ({
                    url: `/v1/practice-areas`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['PracticeAreas', 'ActivePracticeAreas', 'TenantConfiguration'],
            }),
            // toggling isAmlApplicable (or deleting an AML practice area) can create/archive
            // AML case block warning notifications server-side
            updatePracticeArea: builder.mutation<PracticeArea, PracticeAreaUpdateDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/practice-areas/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: ['PracticeAreas', 'ActivePracticeAreas', 'Notifications', 'TenantConfiguration'],
            }),
            deletePracticeArea: builder.mutation<boolean, { id: string }>({
                query: ({ id }) => ({
                    url: `/v1/practice-areas/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['PracticeAreas', 'ActivePracticeAreas', 'Notifications', 'TenantConfiguration'],
            }),
            getSecondLevelPracticeAreasUngrouped: accumulatedQueryWithFilters<PracticeArea>(
                builder,
                '/v1/practice-areas/second-level-ungrouped',
                {
                    sort: ['parentPracticeArea.name,asc', 'name,asc'],
                    providesTags: ['PracticeAreas'],
                }
            ),
        }),
        overrideExisting: false,
    });

export const {
    useGetPracticeAreaByIdQuery,
    useLazyGetPracticeAreasWithFiltersQuery,
    useLazyGetPracticeAreasListPageQuery,
    useLazyGetActivePracticeAreasWithFiltersQuery,
    useUpdatePracticeAreaMutation,
    useAddPracticeAreaMutation,
    useGetPracticeAreasWithFiltersNotPaginatedQuery,
    useLazyGetSecondLevelPracticeAreasUngroupedQuery,
} = APIPracticeAreasSlice;
